// BUTreeNode — recursive tree row for BusinessUnitsPage.
// Split into its own file to avoid a Babel edge-case (Maximum call stack
// size exceeded) that fires when large components hold recursive JSX.
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Building2, ChevronRight, ChevronDown } from 'lucide-react';

const LEVEL_LABELS = {
  company: 'Company',
  business_unit: 'Business Unit',
  department: 'Department',
};

const LEVEL_COLORS = {
  company: 'bg-cyan-500/20 text-cyan-200 border-cyan-500/40',
  business_unit: 'bg-emerald-500/20 text-emerald-200 border-emerald-500/40',
  department: 'bg-amber-500/20 text-amber-200 border-amber-500/40',
};

export default function BUTreeNode({ node, depth, onSelect, selectedId }) {
  const [open, setOpen] = useState(true);
  const children = node.children || [];
  const hasChildren = children.length > 0;
  const isSelected = selectedId === node.id;

  return (
    <div>
      <div
        className={
          'flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors ' +
          (isSelected ? 'bg-cyan-500/15 border border-cyan-500/30' : 'hover:bg-slate-800')
        }
        style={{ marginLeft: depth * 16 }}
        onClick={() => onSelect(node)}
        data-testid={'bu-tree-node-' + node.id}
      >
        {hasChildren ? (
          <button
            type="button"
            className="text-slate-500 hover:text-slate-200"
            onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
            aria-label={open ? 'Collapse' : 'Expand'}
          >
            {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>
        ) : (
          <span style={{ width: 14 }} />
        )}
        <Building2 className="w-4 h-4 text-slate-400" />
        <span className="text-sm text-slate-100 flex-1 truncate">{node.name}</span>
        {node.code ? (
          <span className="font-mono text-[10px] text-slate-500">{node.code}</span>
        ) : null}
        <Badge variant="outline" className={'text-[10px] py-0 h-4 ' + (LEVEL_COLORS[node.level] || '')}>
          {LEVEL_LABELS[node.level] || node.level}
        </Badge>
      </div>
      {open && hasChildren ? (
        <div>
          {children.map((c) => (
            <BUTreeNode
              key={c.id}
              node={c}
              depth={depth + 1}
              onSelect={onSelect}
              selectedId={selectedId}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
